import { useContext, useEffect, useState } from 'react'
import { StoreContext, useAppStore } from '../../shared/store/store'
import { ViewerConfig } from '../../../../trillion-common/src/objects/viewer-config.object'
import { JewelryTypeName } from '../../../../trillion-common/src/typings/jewelry-type'
import { getJewelryPath } from '../../../../trillion-common/src/API/storageAPI'
import { NoBillingPackageError } from '../../../../trillion-common/src/API/errors/NoBillingPackageError'
import { FetchingConfigError } from '../../../../trillion-common/src/API/errors/FetchingConfigError'
import { ViewerAppError } from '../errors/viewer-app-error'
import { TrillionBackend } from '../../../../trillion-common/src/API/trillion-backend'
import { IncorrectActivationKeyError } from '../../../../trillion-common/src/API/errors/IncorrectActivationKeyError'
import { ProductNotExistOrNotPublishedError } from '../../../../trillion-common/src/API/errors/NoProductExistOrNotPublished'

export function useViewerConfig(sku: string, defaultJewelryType: JewelryTypeName) {
  const viewerConfig = useAppStore((state) => state.viewerConfig)
  const setViewerConfig = useAppStore((state) => state.setViewerConfig)
  const store = useContext(StoreContext)
  const [configError, setConfigError] = useState<Error | null>(null)

  const controller = new AbortController()
  const signal = controller.signal

  useEffect(() => {
    async function loadConfig() {
      const startSize = store.getState().startSize

      const configDto = new ViewerConfig(
        await TrillionBackend.getProductConfigForViewer(sku, signal),
      )

      controller.abort()

      if (!configDto.modelPath || configDto.modelPath === '')
        configDto.modelPath = getJewelryPath(sku)
      if (startSize) {
        configDto.pointsOfView.forEach((point) => {
          point.cameraPosition = point.cameraPosition.clone().multiplyScalar(startSize)
        })
      }

      return configDto
    }

    setViewerConfig(undefined) // reset config before loading new
    loadConfig()
      .then((config) => {
        setViewerConfig(config)
      })
      .catch((e) => {
        // console.log(e)
        if (
          e instanceof NoBillingPackageError ||
          e instanceof IncorrectActivationKeyError ||
          e instanceof ProductNotExistOrNotPublishedError
        ) {
          setConfigError(e)
        }
        if (e instanceof FetchingConfigError) {
          setViewerConfig(generateConfig())
        }
      })
  }, [sku])

  function generateConfig() {
    return new ViewerConfig({
      modelPath: getJewelryPath(sku),
      category: defaultJewelryType,
      pointsOfView: [],
      sku: sku,
      lights: [],
      buyItNowEnabled: false,
      linkToProduct: '',
    })
  }

  useEffect(() => {
    // handle async errors to help ErrorBoundary catch them
    if (configError instanceof NoBillingPackageError) {
      throw new ViewerAppError(
        'Problem with the license, please contact the administration',
        configError,
      )
    } else if (configError instanceof IncorrectActivationKeyError) {
      throw new ViewerAppError(
        'Problem with the activation key, please contact the administration',
        configError,
      )
    } else if (configError instanceof ProductNotExistOrNotPublishedError) {
      throw new ViewerAppError(
        'Product is not published, please contact administration',
        configError,
      )
    }
  }, [configError])

  return viewerConfig
}
